/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { View } from 'ol';
import { useRef, useEffect } from 'react';
import { useGetWMLayerInstance } from './useHooksGetCapabilities';
import type { OpenLayersLayerProps } from '../components/OpenLayers/layers';
import { initializeOpenLayersProjections } from '../components/OpenLayers/utils/projections';

initializeOpenLayersProjections();

export const useViewFromLayer = (
  layerProps: OpenLayersLayerProps,
  baseView = {
    projection: 'EPSG:3857',
    zoom: 1,
    center: [0, 0],
  },
): View => {
  const viewRef = useRef<View | null>(null);

  if (viewRef.current === null) {
    try {
      viewRef.current = new View(baseView);
    } catch (_e) {
      viewRef.current = new View({
        projection: 'EPSG:3857',
        zoom: 1,
        center: [0, 0],
      });
      console.warn('Projection not supported');
    }
  }

  const { wmLayer } = useGetWMLayerInstance(layerProps);

  useEffect(() => {
    if (!wmLayer) {
      return;
    }

    const layerProjectionForCode = wmLayer.getProjection(
      viewRef.current!.getProjection().getCode(),
    );
    if (!layerProjectionForCode?.bbox) {
      return;
    }
    viewRef.current!.fit(layerProjectionForCode.bbox.getExtent());
  }, [wmLayer]);
  return viewRef.current!;
};
